import { ApolloQueryResult } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import React, { ReactNode } from 'react';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { isStatuspage as isStatuspageProduct } from '@atlassian/cssxp-utils-product';
import {
  isTrialEntitlement,
  validateFreeSubscriptions,
  isActiveEntitlement,
  getFormattedDate,
} from '@css-xp/entitlements-v2';
import { EntitlementDetails, BillingFrequency } from '@css-xp/graphtypes/src/generated-types-new';
import { NextBillDate as NextBillDateComponent, NoData } from '@css-xp/ppc-common';
import { cardStyles, HeadingText } from './entitlement-cards.styles';

export type NextBillDateProps = {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isGracePeriodVisibilityEnabled?: boolean;
};

export const NextBillDate = ({ entitlement, refetch, isGracePeriodVisibilityEnabled }: NextBillDateProps) => {
  const { productId, billingFrequency } = entitlement;

  const isFreePlan = validateFreeSubscriptions(entitlement);
  const isStatusPage = isStatuspageProduct(productId);
  const isTrial = !isFreePlan && isTrialEntitlement(entitlement, isStatusPage);
  const isEntitlementInGracePeriod = isActiveEntitlement(entitlement) && entitlement?.pauseBilling?.startTimestamp;
  const gracePeriodEndDate = entitlement?.pauseBilling?.endTimestamp;

  let nextBillDateHeading = 'Next bill date';

  if (isTrial) {
    nextBillDateHeading = 'Trial end date';
  } else if (billingFrequency === BillingFrequency.ANNUAL || isEntitlementInGracePeriod) {
    nextBillDateHeading = 'End date';
  }

  let nextBillDateText: string | ReactNode = (
    <NextBillDateComponent entitlement={entitlement} refetch={refetch} hideChangeBillDateButton />
  );
  const endDate = isTrial
    ? entitlement?.subscriptionTimestamps?.lastTrialEndTimestamp
    : entitlement?.latestPeriodEndDate;
  const renewInDays = endDate && differenceInDays(new Date(entitlement.latestPeriodEndDate as string), new Date());

  if (isFreePlan) {
    nextBillDateText = 'N/A';
  }

  return (
    <Box padding="space.200" xcss={cardStyles}>
      <Stack space="space.100" alignBlock="center">
        <Text size="small" color="color.text.subtlest">
          {nextBillDateHeading}
        </Text>
        <HeadingText>{nextBillDateText}</HeadingText>
        {isGracePeriodVisibilityEnabled && isEntitlementInGracePeriod && (
          <>
            <Text size="small" color="color.text.subtlest">
              Deactivation date
            </Text>
            {gracePeriodEndDate ? (
              <Text size="medium" color="color.text.subtlest">
                {getFormattedDate(gracePeriodEndDate.toString())}
              </Text>
            ) : (
              <NoData />
            )}
          </>
        )}
        {renewInDays && renewInDays > 0 && renewInDays <= 30 && (
          <Text color="color.text.subtle">Renews in {renewInDays} days</Text>
        )}
      </Stack>
    </Box>
  );
};
